// HTTP helper for the external API clients.
//
// - 10s default timeout (AbortController).
// - One retry on network error or 5xx. Exponential back-off on 429.
// - Returns a Result<unknown> after parsing JSON; callers cast/validate.

import type { ApiError, Result } from './types';

const DEFAULT_TIMEOUT_MS = 10_000;
const MAX_RETRIES = 1;

export interface FetchJsonOptions {
  /** Override the default timeout. */
  timeoutMs?: number;
  /** Optional headers (e.g., Accept overrides). */
  headers?: Record<string, string>;
  /** AbortSignal from the caller (composed with our timeout). */
  signal?: AbortSignal;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return 'unknown';
  }
}

function composeSignal(timeoutMs: number, external?: AbortSignal): { signal: AbortSignal; cancel: () => void } {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);
  if (external) {
    if (external.aborted) ctrl.abort(external.reason);
    else external.addEventListener('abort', () => ctrl.abort(external.reason), { once: true });
  }
  return { signal: ctrl.signal, cancel: () => clearTimeout(timer) };
}

async function attempt(url: string, opts: FetchJsonOptions): Promise<Result<unknown>> {
  const host = hostOf(url);
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const { signal, cancel } = composeSignal(timeoutMs, opts.signal);
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', ...(opts.headers ?? {}) },
      signal,
    });
    if (res.status === 404) {
      return { ok: false, degraded: true, error: { kind: 'not_found', host, status: 404, message: `${host} returned 404` } };
    }
    if (res.status === 429) {
      return { ok: false, degraded: true, error: { kind: 'rate_limited', host, status: 429, message: `${host} rate limit` } };
    }
    if (res.status >= 500) {
      return { ok: false, degraded: true, error: { kind: 'server_error', host, status: res.status, message: `${host} server error ${res.status}` } };
    }
    if (res.status >= 400) {
      return { ok: false, degraded: true, error: { kind: 'bad_request', host, status: res.status, message: `${host} returned ${res.status}` } };
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return { ok: false, degraded: true, error: { kind: 'invalid_response', host, status: res.status, message: `${host} returned non-JSON` } };
    }
    return { ok: true, data: body };
  } catch (err) {
    const isTimeout = err instanceof DOMException && err.name === 'TimeoutError';
    const kind = isTimeout ? 'timeout' : 'network';
    const message = err instanceof Error ? err.message : 'fetch failed';
    return { ok: false, degraded: true, error: { kind, host, message } };
  } finally {
    cancel();
  }
}

function shouldRetry(err: ApiError): boolean {
  return err.kind === 'network' || err.kind === 'server_error' || err.kind === 'rate_limited' || err.kind === 'timeout';
}

export async function fetchJson(url: string, opts: FetchJsonOptions = {}): Promise<Result<unknown>> {
  let last: Result<unknown> = await attempt(url, opts);
  for (let i = 0; i < MAX_RETRIES && !last.ok && shouldRetry(last.error); i++) {
    const delay = last.error.kind === 'rate_limited' ? 2000 * (i + 1) : 500;
    await new Promise((r) => setTimeout(r, delay));
    last = await attempt(url, opts);
  }
  return last;
}
